import { classInstance } from '@/shared/helpers/helpers';

const mascotStartDelay = 2000;
const visibilityBuffer = -80;
const mascotScrollReplayLoop = true;

export class VideoMascot {
	private readonly element: HTMLElement;
	private video: HTMLVideoElement | null = null;
	private linkedVideo: HTMLVideoElement | null = null;
	private wrapper: HTMLElement | null = null;
	private startTimer: number | null = null;
	private defaultPosition: number | 'end' | null = null;
	private breakpoint: MediaQueryList | null = null;
	private scrollTicking = false;
	private visibilityActive = false;
	private wasMascotVisible = false;
	private wasSectionFullyScrolledPast = false;
	private pendingScrollReplay = false;
	private scrollReplayUsed = false;
	private visibilityReady = false;

	private readonly classHidden = 'about-video-mascot--hidden';

	constructor(element: HTMLElement) {
		this.element = element;

		if (!this.element) return;

		this.init();
	}

	public init(): void {
		classInstance.set(this.element, { videoMascot: this });

		this.video = this.element.querySelector('video');
		this.linkedVideo = this.findLinkedVideo();
		this.wrapper = this.element.closest('.about-video-wrapper');
		this.defaultPosition = this.parseDefaultPosition(this.element.dataset.mascotDefaultPosition);

		this.breakpoint = window.matchMedia(`(width >= ${(window as any).vars.breakpoint.desktop}px)`);
		this.breakpoint.addEventListener('change', this.handleBreakpointChange);

		if (this.breakpoint.matches) {
			this.initVisibility();
		}

		if (!this.video) return;

		this.reset();
		this.linkedVideo?.addEventListener('play', this.handleLinkedPlay);
		this.video.addEventListener('ended', this.handleEnded);
		this.startTimer = window.setTimeout(this.startMascot, mascotStartDelay);
	}

	public play(): void {
		if (!this.video || this.isLinkedPlaying()) return;

		this.video.currentTime = 0;
		this.video.play();
	}

	public pause(): void {
		this.video?.pause();
	}

	public reset(): void {
		if (!this.video) return;

		this.video.pause();

		if (this.defaultPosition !== null) {
			this.setPosition(this.defaultPosition);
		} else {
			this.video.currentTime = 0;
		}
	}

	/**
	 * @desc Позиция видео в покое по умолчанию
	 * @param {string} [value] - секунды (число) или ключевое слово "end" для последнего кадра
	 */
	private parseDefaultPosition(value: string | undefined): number | 'end' | null {
		if (!value) return null;

		if (value === 'end') return 'end';

		const seconds = Number(value);

		return Number.isFinite(seconds) ? seconds : null;
	}

	private setPosition(position: number | 'end'): void {
		if (!this.video) return;

		const apply = (): void => {
			if (position === 'end') {
				this.video!.currentTime = Math.max(0, this.video!.duration - 0.001);
			} else {
				this.video!.currentTime = position;
			}
		};

		if (this.video.readyState >= HTMLMediaElement.HAVE_METADATA) {
			apply();
		} else {
			this.video.addEventListener('loadedmetadata', apply, { once: true });
			this.video.load();
		}
	}

	private findLinkedVideo(): HTMLVideoElement | null {
		const mascotId = this.element.dataset.mascot;

		if (!mascotId) return null;

		const linkedContainer = document.querySelector<HTMLElement>(`.js-video[data-mascot="${mascotId}"]`);

		return linkedContainer?.querySelector('video') ?? null;
	}

	private isLinkedPlaying(): boolean {
		return Boolean(this.linkedVideo && !this.linkedVideo.paused);
	}

	private initVisibility(): void {
		if (this.visibilityActive) return;

		this.visibilityActive = true;
		document.addEventListener('scroll', this.handleScroll, { passive: true, capture: true });
		window.addEventListener('resize', this.handleScroll, { passive: true });
		this.updateStickyTop();
		this.updateVisibility();
	}

	private destroyVisibility(): void {
		if (!this.visibilityActive) return;

		this.visibilityActive = false;
		document.removeEventListener('scroll', this.handleScroll, true);
		window.removeEventListener('resize', this.handleScroll);
		this.element.style.removeProperty('--mascot-sticky-top');
		this.element.classList.remove(this.classHidden);
		this.wasMascotVisible = false;
		this.wasSectionFullyScrolledPast = false;
		this.pendingScrollReplay = false;
		this.scrollReplayUsed = false;
		this.visibilityReady = false;
	}

	private getVisibleTop(): number {
		const header = document.querySelector<HTMLElement>('header');

		if (!header) return 0;

		let top = 0;

		header.querySelectorAll<HTMLElement>('.header__sticky.sticky, .header-top').forEach((element) => {
			if (getComputedStyle(element).position !== 'fixed') return;

			top = Math.max(top, element.getBoundingClientRect().bottom);
		});

		if (top > 0) return top;

		const headerBottom = header.getBoundingClientRect().bottom;

		return headerBottom > 0 ? headerBottom : 0;
	}

	private updateStickyTop(): void {
		this.element.style.setProperty('--mascot-sticky-top', `${this.getVisibleTop()}px`);
	}

	private isAtContainerBottom(containerRect: DOMRect, mascotRect: DOMRect): boolean {
		const marginBottom = parseFloat(getComputedStyle(this.element).marginBottom) || 0;
		const paddingBottom = parseFloat(getComputedStyle(this.element).paddingBottom) || 0;

		return mascotRect.bottom >= containerRect.bottom - marginBottom - paddingBottom - 2;
	}

	private updateVisibility(): void {
		if (!this.wrapper) return;

		this.updateStickyTop();

		const mascotRect = this.element.getBoundingClientRect();
		const containerRect = this.wrapper.getBoundingClientRect();
		const visibleTop = this.getVisibleTop();
		const visibleBottom = window.innerHeight;
		const sectionInView = containerRect.bottom > visibleTop && containerRect.top < visibleBottom;
		const overflowsTop = mascotRect.top < visibleTop + visibilityBuffer;
		const overflowsBottom = mascotRect.bottom > visibleBottom - visibilityBuffer;
		const atContainerBottom = this.isAtContainerBottom(containerRect, mascotRect);
		const shouldHide = !sectionInView || overflowsTop || (atContainerBottom && overflowsBottom) || (containerRect.bottom < visibleBottom + Math.abs(visibilityBuffer) && overflowsBottom);
		const mascotVisible = sectionInView && !shouldHide;
		const sectionFullyScrolledPast = containerRect.bottom < 1;

		if (!mascotScrollReplayLoop && !this.scrollReplayUsed && sectionFullyScrolledPast && !this.wasSectionFullyScrolledPast) {
			this.pendingScrollReplay = true;
		}

		this.wasSectionFullyScrolledPast = sectionFullyScrolledPast;

		this.element.classList.toggle(this.classHidden, shouldHide);

		const becameVisible = mascotVisible && !this.wasMascotVisible;

		if (!mascotVisible && this.wasMascotVisible) {
			this.handleMascotHidden();
		}

		if (this.visibilityReady && becameVisible && sectionInView && this.shouldReplayAfterShow()) {
			if (!mascotScrollReplayLoop) {
				this.pendingScrollReplay = false;
				this.scrollReplayUsed = true;
			}

			this.replayMascot();
		}

		this.wasMascotVisible = mascotVisible;
		this.visibilityReady = true;
	}

	private shouldReplayAfterShow(): boolean {
		if (mascotScrollReplayLoop) {
			return true;
		}

		return this.pendingScrollReplay;
	}

	private handleMascotHidden(): void {
		this.clearStartTimer();
		this.reset();
	}

	private replayMascot(): void {
		if (!this.video || this.isLinkedPlaying()) return;

		this.clearStartTimer();
		this.reset();
		this.startTimer = window.setTimeout(this.startMascot, mascotStartDelay);
	}

	private startMascot = (): void => {
		this.startTimer = null;
		this.play();
	};

	private handleLinkedPlay = (): void => {
		this.reset();
	};

	private handleEnded = (): void => {
		this.reset();
	};

	private handleScroll = (): void => {
		if (this.scrollTicking) return;

		this.scrollTicking = true;

		requestAnimationFrame(() => {
			this.updateVisibility();
			this.scrollTicking = false;
		});
	};

	private handleBreakpointChange = (): void => {
		if (this.breakpoint?.matches) {
			this.initVisibility();
		} else {
			this.destroyVisibility();
		}
	};

	private clearStartTimer(): void {
		if (this.startTimer === null) return;

		clearTimeout(this.startTimer);
		this.startTimer = null;
	}

	public destroy(): void {
		this.clearStartTimer();
		this.linkedVideo?.removeEventListener('play', this.handleLinkedPlay);
		this.video?.removeEventListener('ended', this.handleEnded);
		this.breakpoint?.removeEventListener('change', this.handleBreakpointChange);
		this.destroyVisibility();
		classInstance.del(this.element, 'videoMascot');
	}

	public reinit(): void {
		this.destroy();
		this.init();
	}
}
